// What hiding a ticket says, and what the foot of the feed offers back.
//
// Four sentences, in one place, because two screens say them: the row under a
// ticket on Today (`TicketActions`) and the ticket screen itself
// (`MultiDetailPage`). The two already keep separate copies of the bookmaker
// handoff and that is enough duplication for one act — the words, at least,
// are written once.
//
// THE DIFFERENCE BETWEEN HIDE AND DELETE IS WHOSE TICKET IT IS, and the words
// have to carry it. A strategy's proposal is evidence about that strategy, so
// hiding it takes it off the feed and leaves the paper trail alone — the app
// proposed it, and tomorrow morning it settles like every other proposal. A
// multi the punter built is nobody's opinion but their own: it is not evidence
// about anything, so "Delete" is the honest word and the record goes with it.
//
// Pure, and imports nothing.

/** The action on somebody else's ticket — the app's — and on the punter's own. */
export const HIDE_LABEL = 'Hide';
export const DELETE_LABEL = 'Delete';

/**
 * Which of the two a ticket offers. A ticket the punter built carries
 * `builtByHand` (lib/engine/reprice.js `newTicket`); everything else on the
 * feed was dealt by a strategy.
 */
export const hideLabel = (multi) => (multi?.builtByHand === true ? DELETE_LABEL : HIDE_LABEL);

/**
 * WHERE IT WENT, not merely that it went. The tickets a punter hides are the
 * ones they are least likely to go looking for, so a toast reading "Hidden."
 * alone would leave the way back to be discovered — and a control nobody can
 * find is a control that does not exist. The undo bar is the other half: this
 * sentence is for the reader who did mean it.
 */
export const HIDDEN_LINE = 'Hidden · Show hidden at the foot.';

/** A draft, and the record of a draft. Said plainly: both of them go. */
export const DELETED_LINE = 'Deleted · your ticket, and its record.';

/**
 * Restoring is not the reverse of a tap, because the feed is in kick-off order:
 * a ticket put back lands wherever its first leg starts, which is rarely beside
 * the foot control that put it there.
 */
export const RESTORED_LINE = 'Back on the feed, under its start time.';

/**
 * The foot control: "Show 3 hidden ›". The count is the point — it is the only
 * thing on the screen that says anything was hidden at all — so it comes before
 * the word, and an empty list prints nothing rather than "Show 0 hidden".
 */
export function hiddenFootLabel(n) {
  const count = Number(n) || 0;
  return count > 0 ? `Show ${count} hidden ›` : '';
}

/**
 * One hidden ticket as a line in that list: "Low risk · 3 legs · $8.40".
 *
 * `{ name, legs, odds }` as `hiddenToday` (store.js) answers it. The odds are
 * dropped rather than printed as "$—" where a ticket has none: a figure nobody
 * can read is worse than a shorter line.
 */
export function hiddenRowLine({ name, legs, odds } = {}) {
  const parts = [name || 'Ticket'];
  const n = Number(legs) || 0;
  if (n > 0) parts.push(`${n} leg${n === 1 ? '' : 's'}`);
  const price = Number(odds);
  if (Number.isFinite(price) && price > 1) parts.push(`$${price.toFixed(2)}`);
  return parts.join(' · ');
}
